import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API, { getImageUrl, downloadCsv } from '../services/api';
import { useToast } from '../contexts/ToastContexts';
import ProductFormModal from '../components/ProductFormModal';
import './Products.css';
import { useConfirm } from '../contexts/ConfirmContext';

function Products() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { confirm } = useConfirm();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDiscount, setBulkDiscount] = useState('');
  // Track if error already shown to prevent duplicate toasts
  const errorShown = useRef(false);

  // Fetch products when component mounts
  useEffect(() => {
    fetchProducts();
  }, []);

  // Function to fetch products from API
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await API.get("/products");
      setProducts(response.data);
      errorShown.current = false; // Reset error flag on success
    } catch (error) {
      console.error("Error fetching products:", error);
      if (!errorShown.current) {
        addToast("Failed to load products. Backend may be offline.", "error");
        errorShown.current = true;
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter products based on selection and search
  const getFilteredProducts = () => {
    let filtered = products;
    
    if (filter === 'active') {
      filtered = filtered.filter(p => p.product_IsActive === true);
    }
    if (filter === 'inactive') {
      filtered = filtered.filter(p => p.product_IsActive === false);
    }
    
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.product_Name.toLowerCase().includes(term) ||
        (p.product_SKU && p.product_SKU.toLowerCase().includes(term)) ||
        (p.product_Description && p.product_Description.toLowerCase().includes(term))
      );
    }
    
    return filtered;
  };

  const filteredProducts = getFilteredProducts();

  const formatDiscount = (product) => {
    const value = product.product_DiscountPercent ?? product.category?.category_DiscountPercent;
    if (value == null || value <= 0) return '—';
    const num = Number(value);
    const label = Number.isInteger(num) ? `${num}%` : `${num.toFixed(1)}%`;
    if (product.product_DiscountPercent) return label;
    return `${label} (cat)`;
  };

const handleDelete = async (id, productName) => {
  const userConfirmed = await confirm(`Are you sure you want to delete "${productName}"?`);
  if (!userConfirmed) return;

  try {
    await API.delete(`/products/${id}`);
    addToast("Product deleted successfully", "success");
    fetchProducts();
  } catch (error) {
    const message = error.response?.data?.message || "Failed to delete product";
    addToast(message, "error");
  }
};

  const handleAdd = () => {
    setEditingProduct(null);
    setShowModal(true);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProduct(null);
  };

  const handleSaveProduct = async (productData) => {
    try {
      const productId = editingProduct?.product_Id || productData.product_Id;

      if (productId) {
        const updateData = {
          ...productData,
          product_Id: productId
        };

        await API.put(`/products/${productId}`, updateData);
        addToast("Product updated successfully", "success");
        handleCloseModal();
        fetchProducts();
        return updateData;
      }

      const response = await API.post("/products", productData);
      fetchProducts();
      return response.data;
    } catch (error) {
      console.error("Error saving product:", error);
      const errorMessage = error.response?.data || "Failed to save product";
      addToast(errorMessage, "error");
      throw error;
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredProducts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredProducts.map(p => p.product_Id));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) {
      addToast('Select at least one product', 'error');
      return;
    }
    try {
      const payload = { productIds: selectedIds, action };
      if (action === 'setDiscount') {
        payload.discountPercent = parseFloat(bulkDiscount);
      }
      await API.post('/products/bulk-action', payload);
      addToast(`${selectedIds.length} product(s) updated`, 'success');
      setSelectedIds([]);
      fetchProducts();
    } catch (error) {
      addToast(error.response?.data?.message || 'Bulk action failed', 'error');
    }
  };

  const handleExport = async () => {
    try {
      await downloadCsv('/products/export/csv', `products-${new Date().toISOString().slice(0, 10)}.csv`);
      addToast('Products exported', 'success');
    } catch (error) {
      addToast('Export failed', 'error');
    }
  };

  const goBack = () => {
    navigate('/');
  };

  return (
    <div className="products-container">
      <div className="products-header">
        <div className="header-left">
          <button className="back-button" onClick={goBack}>
            ← Back
          </button>
          <h1>Manage Products</h1>
        </div>
        <div className="header-right">
          <div className="filter-buttons">
            <button 
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All ({products.length})
            </button>
            <button 
              className={`filter-btn ${filter === 'active' ? 'active' : ''}`}
              onClick={() => setFilter('active')}
            >
              Active ({products.filter(p => p.product_IsActive === true).length})
            </button>
            <button 
              className={`filter-btn ${filter === 'inactive' ? 'active' : ''}`}
              onClick={() => setFilter('inactive')}
            >
              Inactive ({products.filter(p => p.product_IsActive === false).length})
            </button>
          </div>
          <div className="search-box">
            <input
              type="text"
              placeholder="🔍 Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button className="clear-search" onClick={() => setSearchTerm('')}>✕</button>
            )}
          </div>
          <button className="add-button" onClick={handleAdd}>
            + Add Product
          </button>
          <button className="export-button" onClick={handleExport}>Export CSV</button>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="bulk-actions-bar">
          <span>{selectedIds.length} selected</span>
          <button onClick={() => handleBulkAction('activate')}>Activate</button>
          <button onClick={() => handleBulkAction('deactivate')}>Deactivate</button>
          <input type="number" placeholder="Discount %" value={bulkDiscount} onChange={(e) => setBulkDiscount(e.target.value)} min="0" max="100" />
          <button onClick={() => handleBulkAction('setDiscount')}>Set Discount</button>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading products...</div>
      ) : (
        <div className="products-table-container">
          <table className="products-table">
            <thead>
              <tr>
                <th><input type="checkbox" checked={filteredProducts.length > 0 && selectedIds.length === filteredProducts.length} onChange={toggleSelectAll} /></th>
                <th>Image</th>
                <th>Name</th>
                <th>Category</th>
                <th>Price</th>
                <th>Discount</th>
                <th>Stock</th>
                <th>Shipping Fee</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan="10" className="no-data">
                    {products.length === 0 
                      ? 'No products found. Click "Add Product" to create one.' 
                      : `No ${filter !== 'all' ? filter : ''} products match your search.`}
                  </td>
                </tr>
              ) : (
                filteredProducts.map((product) => (
                  <tr key={product.product_Id}>
                    <td><input type="checkbox" checked={selectedIds.includes(product.product_Id)} onChange={() => toggleSelect(product.product_Id)} /></td>
                    <td className="product-image-cell">
                      {product.productImages && product.productImages.length > 0 ? (
                        <img 
                          src={getImageUrl(product.productImages[0].productImage_ImageUrl)} 
                          alt={product.product_Name}
                          className="product-image-thumb"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : (
                        <div className="product-image-placeholder">No Image</div>
                      )}
                    </td>
                    <td className="product-name-cell" data-label="Name">
                      {product.product_Name}
                    </td>
                    <td className="product-category-cell" data-label="Category">
  {product.category?.category_Name || 'Uncategorized'}
</td>
                    <td className="product-price-cell" data-label="Price">
                      {product.product_PriceUSD 
                        ? `$${product.product_PriceUSD.toFixed(2)}` 
                        : product.product_PriceLBP 
                          ? `${product.product_PriceLBP.toFixed(2)} LBP`
                          : 'N/A'}
                    </td>
                    <td data-label="Discount">{formatDiscount(product)}</td>
                    <td className="product-stock-cell" data-label="Stock">
                      {product.product_Stock}
                    </td>
                    <td className="product-shipping-cell" data-label="Shipping Fee">
  {product.product_ShippingFee !== null && product.product_ShippingFee !== undefined ? (
    product.product_PriceUSD 
      ? `$${product.product_ShippingFee.toFixed(2)}`
      : `${product.product_ShippingFee.toFixed(2)} LBP`
  ) : (
    'Free'
  )}
</td>
                    <td className="product-status-cell" data-label="Status">
                      <span className={product.product_IsActive ? 'status-active' : 'status-inactive'}>
                        {product.product_IsActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="product-actions-cell" data-label="Actions">
                      <button className="edit-btn" onClick={() => handleEdit(product)}>Edit</button>
                      <button className="delete-btn" onClick={() => handleDelete(product.product_Id, product.product_Name)}>Delete</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <ProductFormModal
          product={editingProduct}
          onSave={handleSaveProduct}
          onClose={handleCloseModal}
          onRefresh={fetchProducts}
        />
      )}
    </div>
  );
}

export default Products;